// react-refresh: non-component export in component file
/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense, createContext, useContext, type ComponentType, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { TenantProvider } from '@/contexts/TenantContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useTenant } from '@/hooks/useTenant';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { roleLabels } from '@/types/roles';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { getOrganizationBillingState } from '@/lib/billing-access';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';
import { AppointmentModificationPromptHost } from '@/components/appointments/AppointmentModificationPromptHost';
import { DevEnvironmentBadge } from '@/components/DevEnvironmentBadge';
import { formatDateValue } from '@/lib/locale';
import PublicBookingPage from './pages/PublicBookingPage';

const SUPPORT_WHATSAPP_URL = import.meta.env.VITE_SUPPORT_WHATSAPP_URL?.trim() || '#';

// ── Billing banner context ────────────────────────────────────────────────────
// Lets Header and layout know that the banner is visible so they can offset
export const BillingBannerContext = createContext(false);
export function useBillingBanner() { return useContext(BillingBannerContext); }

// Sentry MESTLY-APP-P/E/C/5: "Failed to fetch dynamically imported module",
// "Importing a module script failed", "'text/html' is not a valid
// JavaScript MIME type" -- las tres son el mismo caso: una pestaña quedo
// abierta desde antes de un deploy nuevo y pide un chunk con un hash que ya
// no existe (el servidor devuelve el index.html de fallback en su lugar).
// La unica salida real es recargar para traer el index.html nuevo con los
// hashes correctos -- se intenta una sola vez por sesion de navegador para
// no entrar en loop si el error es de otra naturaleza.
const CHUNK_RELOAD_FLAG = 'mestly_chunk_reload_attempted';

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /fetch dynamically imported module|importing a module script failed|is not a valid javascript mime type/i.test(message);
}

function lazyNamed<TModule extends Record<string, ComponentType<object>>>(
  loader: () => Promise<TModule>,
  exportName: keyof TModule,
) {
  return lazy(async () => {
    try {
      const module = await loader();
      sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
      return { default: module[exportName] };
    } catch (error) {
      const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1';
      if (!alreadyReloaded && isChunkLoadError(error)) {
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
        window.location.reload();
        // La pagina esta a punto de recargar -- no resolver ni rechazar,
        // para que React no llegue a renderizar el estado de error.
        await new Promise(() => {});
      }
      throw error;
    }
  });
}

const IndustryLandingPage = lazyNamed(() => import('@/pages/IndustryLandingPage'), 'IndustryLandingPage');
const PrivacyPage = lazyNamed(() => import('@/pages/PrivacyPage'), 'PrivacyPage');
const TermsPage = lazyNamed(() => import('@/pages/TermsPage'), 'TermsPage');
const Dashboard = lazyNamed(() => import('@/pages/Dashboard'), 'Dashboard');
const AppointmentsPage = lazyNamed(() => import('@/pages/AppointmentsPage'), 'AppointmentsPage');
const ClientsPage = lazyNamed(() => import('@/pages/ClientsPage'), 'ClientsPage');
const CRMPage = lazyNamed(() => import('@/pages/CRMPage'), 'CRMPage');
const CampaignEditorPage = lazyNamed(() => import('@/pages/CampaignEditorPage'), 'CampaignEditorPage');
const LoyaltyPage = lazyNamed(() => import('@/pages/LoyaltyPage'), 'LoyaltyPage');
const ProfessionalsPage = lazyNamed(() => import('@/pages/ProfessionalsPage'), 'ProfessionalsPage');
const ServicesPage = lazyNamed(() => import('@/pages/ServicesPage'), 'ServicesPage');
const InventoryPage = lazyNamed(() => import('@/pages/InventoryPage'), 'InventoryPage');
const ProductPerformancePage = lazyNamed(() => import('@/pages/ProductPerformancePage'), 'ProductPerformancePage');
const FinancesPage = lazyNamed(() => import('@/pages/FinancesPage'), 'FinancesPage');
const CommissionsReportPage = lazyNamed(() => import('@/pages/CommissionsReportPage'), 'CommissionsReportPage');
const ReportsPage = lazyNamed(() => import('@/pages/ReportsPage'), 'ReportsPage');
const SettingsPage = lazyNamed(() => import('@/pages/SettingsPage'), 'SettingsPage');
const ConfirmAppointmentPage = lazyNamed(() => import('@/pages/ConfirmAppointmentPage'), 'ConfirmAppointmentPage');
const UnsubscribePage = lazyNamed(() => import('@/pages/UnsubscribePage'), 'UnsubscribePage');
const CampaignLinkRedirectPage = lazyNamed(() => import('@/pages/CampaignLinkRedirectPage'), 'CampaignLinkRedirectPage');
const ReviewPage = lazyNamed(() => import('@/pages/ReviewPage'), 'ReviewPage');
const PublicClassSchedulePage = lazy(() => import('@/pages/PublicClassSchedulePage'));
const PatientPortalPage = lazy(() => import('@/pages/PatientPortalPage'));
const ClientPortalPage  = lazy(() => import('@/pages/ClientPortalPage'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const ChangePasswordPage = lazyNamed(() => import('@/pages/ChangePasswordPage'), 'ChangePasswordPage');
const RegisterPage = lazyNamed(() => import('@/pages/RegisterPage'), 'RegisterPage');
const ConfirmEmailPage = lazyNamed(() => import('@/pages/ConfirmEmailPage'), 'ConfirmEmailPage');
const ResetPasswordPage = lazyNamed(() => import('@/pages/ResetPasswordPage'), 'ResetPasswordPage');
const LoginPage = lazyNamed(() => import('@/pages/LoginPage'), 'LoginPage');
const ProPortal = lazyNamed(() => import('@/pages/ProPortal/index'), 'ProPortal');
const BillingPage = lazyNamed(() => import('@/pages/BillingPage'), 'BillingPage');
const MembershipsPage = lazyNamed(() => import('@/pages/MembershipsPage'), 'MembershipsPage');
const GroupClassesPage = lazyNamed(() => import('@/pages/GroupClassesPage'), 'GroupClassesPage');
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      retryDelay: 1000,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0, // Las mutaciones NO deben reintentarse automáticamente
    },
  },
});

function RouteLoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-pulse-soft text-primary">Cargando modulo...</div>
    </div>
  );
}

function DashboardPageFallback() {
  return (
    <DashboardLayout>
      <div className="flex min-h-[calc(100vh-6rem)] items-center justify-center">
        <div className="animate-pulse-soft text-primary">Cargando modulo...</div>
      </div>
    </DashboardLayout>
  );
}

function PublicRouteContent({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      {children}
    </Suspense>
  );
}

function DashboardRouteContent({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<DashboardPageFallback />}>
      <DashboardLayout>{children}</DashboardLayout>
    </Suspense>
  );
}


function InactiveAccountPage() {
  const { signOut } = useAuth();

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="max-w-md text-center">
        <h2 className="mb-2 text-2xl font-bold">Usuario Inactivo</h2>
        <p className="mb-4 text-muted-foreground">
          Tu cuenta ha sido desactivada o quedo asociada a una sesion anterior. Cierra sesion para volver a ingresar con la cuenta correcta.
        </p>
        <Button onClick={() => void signOut()}>
          Cerrar sesion y volver al inicio
        </Button>
      </div>
    </div>
  );
}

function SuspendedAccountPage() {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="max-w-md text-center">
        <h2 className="mb-2 text-2xl font-bold">Cuenta Suspendida</h2>
        <p className="mb-4 text-muted-foreground">
          Tu organizacion ha sido suspendida. Contacta a soporte para resolver esta situacion.
        </p>
        <a href="mailto:soporte@mestly.app" className="text-primary hover:underline">
          soporte@mestly.app
        </a>
      </div>
    </div>
  );
}

function BillingBanner({
  effectiveStatus,
  daysLeft,
}: {
  effectiveStatus: 'trialing' | 'past_due';
  daysLeft: number;
  coverageEndLabel: string | null;
}) {
  const isTrial = effectiveStatus === 'trialing';

  return (
    <div className={`fixed top-0 left-0 right-0 z-[45] flex items-center justify-center gap-1.5 px-4 h-10 text-xs sm:text-sm ${
      isTrial ? 'bg-amber-400 text-amber-950' : 'bg-orange-600 text-white'
    }`}>
      {isTrial ? (
        <p className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-center leading-tight">
          <span>😃 Te quedan <strong>{daysLeft <= 0 ? 'menos de 1 día' : `${daysLeft} día${daysLeft === 1 ? '' : 's'}`}</strong> de prueba gratuita.</span>
          <span className="hidden sm:inline">·</span>
          <span className="hidden sm:inline">¿Te está gustando Mestly?</span>
          <a href="/billing" className="font-bold underline underline-offset-2 hover:no-underline whitespace-nowrap">
            Contrata tu plan acá
          </a>
          <span>·</span>
          <span className="hidden sm:inline">¿Necesitas ayuda?</span>
          <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
             className="font-bold underline underline-offset-2 hover:no-underline whitespace-nowrap">
            👉 Contáctanos
          </a>
        </p>
      ) : (
        <p className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-center leading-tight">
          <span>🕒 Lo sentimos 😔, tu período de prueba expiró.</span>
          <a href="/billing" className="font-bold underline underline-offset-2 hover:no-underline whitespace-nowrap">
            Contrata tu plan acá
          </a>
          <span>·</span>
          <a href={SUPPORT_WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
             className="font-bold underline underline-offset-2 hover:no-underline whitespace-nowrap">
            👉 Contáctanos
          </a>
        </p>
      )}
    </div>
  );
}

function ProtectedRoute({ children, requiredModule }: { children: React.ReactNode; requiredModule?: string }) {
  const { user, loading } = useAuth();
  const { canAccessModule, tenantLoading } = usePermissions();
  const { currentOrganization } = useTenant();

  if (loading || tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse-soft text-primary">Cargando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (user.role === 'super_admin') {
    const adminUrl = import.meta.env.VITE_ADMIN_URL ?? 'https://paneladmin.mestly.app';
    if (!adminUrl.includes('localhost')) {
      window.location.replace(adminUrl);
      return null;
    }
  }

  if (user.must_change_password) {
    return <Navigate to="/change-password" replace />;
  }

  if (!user.is_active) {
    return <Navigate to="/inactive" replace />;
  }

  if (currentOrganization && !currentOrganization.is_active) {
    return <SuspendedAccountPage />;
  }

  const billingState = getOrganizationBillingState(currentOrganization);
  const shouldGateByBilling =
    user.role !== 'super_admin'
    && Boolean(currentOrganization)
    && !billingState.hasAccess;

  if (shouldGateByBilling) {
    return <Navigate to="/billing" replace />;
  }

  if (requiredModule && !canAccessModule(requiredModule)) {
    const moduleMessage = currentOrganization
      ? 'Tu plan actual o la configuracion de tu organizacion no incluye este modulo.'
      : 'No tienes una organizacion activa asignada para acceder a este modulo.';

    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-2">Acceso Denegado</h2>
          <p className="text-muted-foreground mb-4">
            {moduleMessage}
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            Rol actual: <span className="font-medium">{roleLabels[user.role]}</span>
          </p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="text-primary hover:underline"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isAdminRole = user.role === 'administrador_gerencial' || user.role === 'administrador_recepcionista';
  if (isAdminRole && (billingState.effectiveStatus === 'trialing' || billingState.effectiveStatus === 'past_due')) {
    const coverageEndLabel = billingState.currentPeriodEnd
      ? formatDateValue(billingState.currentPeriodEnd, currentOrganization ?? undefined, { dateStyle: 'medium' })
      : null;

    return (
      <BillingBannerContext.Provider value={true}>
        <BillingBanner
          effectiveStatus={billingState.effectiveStatus}
          daysLeft={billingState.trialDaysLeft}
          coverageEndLabel={coverageEndLabel}
        />
        {/* pt-10 pushes the entire layout down so the fixed banner (h-10) doesn't overlap */}
        <div className="pt-10">
          {children}
        </div>
      </BillingBannerContext.Provider>
    );
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse-soft text-primary">Cargando...</div>
      </div>
    );
  }

  return (
    <SentryRoutes>
      <Route path="/book/:salonCode" element={<PublicRouteContent><PublicBookingPage /></PublicRouteContent>} />
      <Route path="/schedule/:salonCode" element={<PublicRouteContent><PublicClassSchedulePage /></PublicRouteContent>} />
      <Route path="/patient/:token" element={<PublicRouteContent><PatientPortalPage /></PublicRouteContent>} />
      <Route path="/cliente/:token" element={<PublicRouteContent><ClientPortalPage /></PublicRouteContent>} />
      <Route path="/confirm-appointment" element={<PublicRouteContent><ConfirmAppointmentPage /></PublicRouteContent>} />
      <Route path="/unsubscribe" element={<PublicRouteContent><UnsubscribePage /></PublicRouteContent>} />
      <Route path="/l" element={<PublicRouteContent><CampaignLinkRedirectPage /></PublicRouteContent>} />
      <Route path="/review/:token" element={<PublicRouteContent><ReviewPage /></PublicRouteContent>} />
      <Route path="/confirm-email" element={<PublicRouteContent><ConfirmEmailPage /></PublicRouteContent>} />
      <Route path="/industrias/:industrySlug" element={<PublicRouteContent><IndustryLandingPage /></PublicRouteContent>} />
      <Route path="/privacy" element={<PublicRouteContent><PrivacyPage /></PublicRouteContent>} />
      <Route path="/terms" element={<PublicRouteContent><TermsPage /></PublicRouteContent>} />

      <Route
        path="/register"
        element={
          user
            ? <Navigate to={user.is_active ? '/dashboard' : '/inactive'} replace />
            : <PublicRouteContent><RegisterPage /></PublicRouteContent>
        }
      />

      <Route path="/" element={<Navigate to="/login" replace />} />

      <Route path="/inactive" element={user ? <InactiveAccountPage /> : <Navigate to="/" replace />} />
      <Route path="/billing" element={user ? <PublicRouteContent><BillingPage /></PublicRouteContent> : <Navigate to="/" replace />} />
      <Route path="/change-password" element={user ? <PublicRouteContent><ChangePasswordPage /></PublicRouteContent> : <Navigate to="/" replace />} />
      <Route path="/reset-password" element={<PublicRouteContent><ResetPasswordPage /></PublicRouteContent>} />
      <Route
        path="/login"
        element={
          user
            ? <Navigate to="/dashboard" replace />
            : <PublicRouteContent><LoginPage /></PublicRouteContent>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredModule="appointments">
            <DashboardRouteContent>
              <Dashboard />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments"
        element={
          <ProtectedRoute requiredModule="appointments">
            <DashboardRouteContent>
              <AppointmentsPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute requiredModule="clients">
            <DashboardRouteContent>
              <ClientsPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm"
        element={
          <ProtectedRoute requiredModule="crm">
            <DashboardRouteContent>
              <CRMPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/campanas/nueva"
        element={
          <ProtectedRoute requiredModule="crm">
            <DashboardRouteContent>
              <CampaignEditorPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/campanas/:campaignId/editar"
        element={
          <ProtectedRoute requiredModule="crm">
            <DashboardRouteContent>
              <CampaignEditorPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/loyalty"
        element={
          <ProtectedRoute requiredModule="loyalty">
            <DashboardRouteContent>
              <LoyaltyPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/memberships"
        element={
          <ProtectedRoute requiredModule="loyalty">
            <DashboardRouteContent>
              <MembershipsPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/group-classes"
        element={
          <ProtectedRoute requiredModule="appointments">
            <DashboardRouteContent>
              <GroupClassesPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/professionals"
        element={
          <ProtectedRoute requiredModule="professionals">
            <DashboardRouteContent>
              <ProfessionalsPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/services"
        element={
          <ProtectedRoute requiredModule="services">
            <DashboardRouteContent>
              <ServicesPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute requiredModule="inventory">
            <DashboardRouteContent>
              <InventoryPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/product-performance"
        element={
          <ProtectedRoute requiredModule="inventory">
            <DashboardRouteContent>
              <ProductPerformancePage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/finances"
        element={
          <ProtectedRoute requiredModule="finances">
            <DashboardRouteContent>
              <FinancesPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/finances/commissions-report"
        element={
          <ProtectedRoute requiredModule="finances">
            <DashboardRouteContent>
              <CommissionsReportPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute requiredModule="reports">
            <DashboardRouteContent>
              <ReportsPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute requiredModule="users">
            <DashboardRouteContent>
              <UsersPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute requiredModule="settings">
            <DashboardRouteContent>
              <SettingsPage />
            </DashboardRouteContent>
          </ProtectedRoute>
        }
      />
      {/* /super-admin movido al admin app independiente (localhost:3001 / paneladmin.mestly.app) */}
      <Route path="/pro" element={<PublicRouteContent><ProPortal /></PublicRouteContent>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </SentryRoutes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <TenantProvider>
              <AppRoutes />
              <AppointmentModificationPromptHost />
              <Toaster position="top-right" richColors />
              <PWAInstallPrompt />
              <DevEnvironmentBadge />
            </TenantProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
